import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../utils/supabase';
import { prepareLogo, removeStoredImage, uploadImage } from '../../../utils/storage';
import { centsToInput, money, toCents, UNITS } from '../../../utils/invoices';
import { errorBox, inputClass, noticeBox } from '../../AuthLayout';
import { ImagePicker } from '../../EditorParts';

// Admin → Invoices setup tabs: Business profile, Clients, Products & services, Taxes.

export const btnPrimary = 'h-10 px-4 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-blue-dark disabled:opacity-50';
export const btnSecondary = 'h-10 px-4 rounded-lg border border-line-strong bg-white text-sm font-semibold text-ink hover:bg-surface disabled:opacity-50';

export function Field({ label, hint, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-medium text-ink mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-ink-muted mt-1">{hint}</span>}
    </label>
  );
}
export function Card({ title, children, actions }) {
  return (
    <section className="bg-white rounded-2xl border border-line p-5 sm:p-6 space-y-4">
      {(title || actions) && (
        <div className="flex flex-wrap items-center gap-3">
          {title && <h2 className="font-semibold text-ink flex-1">{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/* ------------------------------------------------------- business profile */

export function BusinessProfile({ seller, onSaved }) {
  const [form, setForm] = useState(seller);
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const logoPreview = useMemo(() => (logoFile ? URL.createObjectURL(logoFile) : form.logo_url || null), [logoFile, form.logo_url]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSaved(false);
    try {
      let logo_url = form.logo_url || null;
      if (logoFile) logo_url = await uploadImage('brand-logos', seller.id, await prepareLogo(logoFile));
      const fields = {
        name: form.name?.trim(), email: form.email || null, phone: form.phone || null, website: form.website || null,
        address: form.address || null, ein: form.ein || null, show_ein: !!form.show_ein, logo_url,
        check_payable_to: form.check_payable_to || null, remit_address: form.remit_address || null,
        default_terms_days: Number(form.default_terms_days || 0), number_prefix: form.number_prefix || '',
        next_number: Math.max(1, Number(form.next_number || 1)), invoice_notes: form.invoice_notes || null,
        invoice_footer: form.invoice_footer || null, email_from_name: form.email_from_name || null, updated_at: new Date().toISOString(),
      };
      const { data, error: err } = await supabase.from('sellers').update(fields).eq('id', seller.id).select().single();
      if (err) throw err;
      if (seller.logo_url && seller.logo_url !== logo_url) await removeStoredImage('brand-logos', seller.logo_url).catch(() => {});
      setLogoFile(null);
      setSaved(true);
      onSaved(data);
    } catch (err) {
      setError(err.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5 max-w-4xl">
      <Card title="Your business">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Business name (on invoices)"><input className={inputClass} value={form.name || ''} onChange={set('name')} required /></Field>
          <Field label="Email shown on invoices" hint="Replies to invoice emails go here."><input type="email" className={inputClass} value={form.email || ''} onChange={set('email')} /></Field>
          <Field label="Phone"><input className={inputClass} value={form.phone || ''} onChange={set('phone')} /></Field>
          <Field label="Website"><input className={inputClass} value={form.website || ''} onChange={set('website')} /></Field>
          <Field label="Business address"><textarea rows={3} className={inputClass} value={form.address || ''} onChange={set('address')} /></Field>
          <div className="space-y-3">
            <Field label="EIN (tax ID)"><input className={inputClass} value={form.ein || ''} onChange={set('ein')} /></Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.show_ein} onChange={set('show_ein')} /> Show the EIN on invoices</label>
          </div>
        </div>
        <div>
          <span className="block text-sm font-medium text-ink mb-2">Logo</span>
          <ImagePicker preview={logoPreview} onPick={setLogoFile} disabled={saving} label={logoPreview ? 'Change logo' : 'Upload logo'} allowSvg
            help="PNG, JPG, WebP or SVG." onRemove={() => { setLogoFile(null); setForm((f) => ({ ...f, logo_url: null })); }} />
        </div>
      </Card>
      <Card title="Getting paid by check">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Make checks payable to"><input className={inputClass} value={form.check_payable_to || ''} onChange={set('check_payable_to')} placeholder={form.name} /></Field>
          <Field label="Mail checks to" hint="Leave empty to use the business address."><textarea rows={3} className={inputClass} value={form.remit_address || ''} onChange={set('remit_address')} /></Field>
        </div>
      </Card>
      <Card title="Invoice defaults">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Payment terms (days)" hint="0 = due on receipt."><input type="number" min="0" max="365" className={inputClass} value={form.default_terms_days ?? 30} onChange={set('default_terms_days')} /></Field>
          <Field label="Number prefix" hint={`Next: ${form.number_prefix || ''}${new Date().getFullYear()}-${String(form.next_number || 1).padStart(4, '0')}`}><input className={inputClass} value={form.number_prefix || ''} onChange={set('number_prefix')} /></Field>
          <Field label="Next number"><input type="number" min="1" className={inputClass} value={form.next_number || 1} onChange={set('next_number')} /></Field>
          <Field label="Sender name on emails" className="sm:col-span-3"><input className={inputClass} value={form.email_from_name || ''} onChange={set('email_from_name')} placeholder={form.name} /></Field>
          <Field label="Default notes on new invoices" className="sm:col-span-3"><textarea rows={2} className={inputClass} value={form.invoice_notes || ''} onChange={set('invoice_notes')} /></Field>
          <Field label="Footer (every invoice)" className="sm:col-span-3"><textarea rows={2} className={inputClass} value={form.invoice_footer || ''} onChange={set('invoice_footer')} /></Field>
        </div>
      </Card>
      {error && <div className={errorBox}>{error}</div>}
      {saved && <div className={noticeBox}>Saved. New invoices use these details; invoices already sent keep theirs.</div>}
      <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save business profile'}</button>
    </form>
  );
}

/* ---------------------------------------------------------------- clients */

const emptyClient = { name: '', contact_name: '', emails: '', phone: '', address: '', tax_exempt: false, tax_exempt_id: '', terms_days: '', org_id: '', notes: '' };

export function Clients({ seller, clients, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [search, setSearch] = useState('');
  useEffect(() => {
    supabase.from('organizations').select('id, name, kind').order('name').then(({ data }) => setOrgs(data || []));
  }, []);
  const shown = clients.filter((c) => !search || `${c.name} ${c.contact_name || ''} ${(c.emails || []).join(' ')}`.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="space-y-5">
      {editing && <ClientForm seller={seller} client={editing} orgs={orgs} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
      <Card title="Clients" actions={<>
        <input type="search" placeholder="Search clients" value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputClass} w-56`} />
        <button type="button" className={btnPrimary} onClick={() => setEditing({ ...emptyClient })}>New client</button>
      </>}>
        {shown.length === 0 ? <p className="text-sm text-ink-muted">No clients yet.</p> : (
          <div className="divide-y divide-line">
            {shown.map((c) => (
              <button key={c.id} type="button" onClick={() => setEditing({ ...c, emails: (c.emails || []).join(', '), terms_days: c.terms_days ?? '', org_id: c.org_id || '' })}
                className="w-full text-left py-3 flex flex-wrap items-center gap-x-4 gap-y-1 hover:bg-surface px-2 rounded-lg">
                <span className="font-semibold text-ink flex-1 min-w-[200px]">{c.name}{c.archived && <span className="ml-2 text-xs text-ink-muted">archived</span>}</span>
                <span className="text-sm text-ink-muted">{c.contact_name}</span>
                <span className="text-sm text-ink-muted">{(c.emails || []).join(', ')}</span>
                {c.tax_exempt && <span className="text-xs font-semibold text-blue">Tax exempt</span>}
                {c.organizations?.name && <span className="text-xs font-semibold text-ink-muted">Carduzo: {c.organizations.name}</span>}
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function ClientForm({ seller, client, orgs, onCancel, onSaved }) {
  const [form, setForm] = useState(client);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    const emails = String(form.emails || '').split(/[,;\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
    const bad = emails.find((x) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x));
    if (bad) return setError(`"${bad}" isn't a valid email.`);
    setSaving(true); setError('');
    const fields = {
      seller_id: seller.id, name: form.name.trim(), contact_name: form.contact_name || null, emails, phone: form.phone || null,
      address: form.address || null, tax_exempt: !!form.tax_exempt, tax_exempt_id: form.tax_exempt ? form.tax_exempt_id || null : null,
      terms_days: form.terms_days === '' ? null : Number(form.terms_days), org_id: form.org_id || null, notes: form.notes || null,
      archived: !!form.archived, updated_at: new Date().toISOString(),
    };
    const { error: err } = form.id
      ? await supabase.from('invoice_clients').update(fields).eq('id', form.id)
      : await supabase.from('invoice_clients').insert(fields);
    setSaving(false);
    if (err) setError(err.message); else onSaved();
  };
  return (
    <form onSubmit={save}>
      <Card title={form.id ? `Edit ${client.name}` : 'New client'}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Business name"><input className={inputClass} value={form.name} onChange={set('name')} required /></Field>
          <Field label="Contact person"><input className={inputClass} value={form.contact_name || ''} onChange={set('contact_name')} /></Field>
          <Field label="Email(s) for invoices" hint="Separate several with commas."><input className={inputClass} value={form.emails} onChange={set('emails')} /></Field>
          <Field label="Phone"><input className={inputClass} value={form.phone || ''} onChange={set('phone')} /></Field>
          <Field label="Billing address"><textarea rows={3} className={inputClass} value={form.address || ''} onChange={set('address')} /></Field>
          <div className="space-y-3">
            <Field label="Payment terms (days)" hint={`Empty = your default (${seller.default_terms_days} days).`}><input type="number" min="0" max="365" className={inputClass} value={form.terms_days} onChange={set('terms_days')} /></Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.tax_exempt} onChange={set('tax_exempt')} /> Tax exempt (no sales tax)</label>
            {form.tax_exempt && <Field label="Exemption certificate number"><input className={inputClass} value={form.tax_exempt_id || ''} onChange={set('tax_exempt_id')} /></Field>}
          </div>
          <Field label="Linked Carduzo account" hint="Lets an invoice switch on their Carduzo access.">
            <select className={inputClass} value={form.org_id} onChange={set('org_id')}>
              <option value="">None</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}{o.kind === 'personal' ? ' (personal)' : ''}</option>)}
            </select>
          </Field>
          <Field label="Private notes"><textarea rows={3} className={inputClass} value={form.notes || ''} onChange={set('notes')} /></Field>
          {form.id && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.archived} onChange={set('archived')} /> Archived (hidden when creating invoices)</label>}
        </div>
        {error && <div className={errorBox}>{error}</div>}
        <div className="flex gap-2.5">
          <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save client'}</button>
          <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
        </div>
      </Card>
    </form>
  );
}

/* --------------------------------------------------------------- products */

const emptyProduct = { name: '', description: '', price: '', unit: 'each', taxable: true, product_line: 'Carduzo', tapzo_plan: '' };

export function Products({ seller, products, onChanged }) {
  const [editing, setEditing] = useState(null);
  const lines = [...new Set(['Carduzo', 'Doggy Canvas', ...products.map((p) => p.product_line)])];
  return (
    <div className="space-y-5">
      {editing && <ProductForm seller={seller} product={editing} lines={lines} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
      <Card title="Products & services" actions={<button type="button" className={btnPrimary} onClick={() => setEditing({ ...emptyProduct })}>New product</button>}>
        {products.length === 0 ? <p className="text-sm text-ink-muted">Nothing yet. Add what you sell, e.g. "Carduzo Teams (per person, yearly)" or "Printed NFC card".</p> : (
          <div className="divide-y divide-line">
            {products.map((p) => (
              <button key={p.id} type="button" onClick={() => setEditing({ ...p, price: centsToInput(p.unit_price_cents), tapzo_plan: p.tapzo_plan || '' })}
                className="w-full text-left py-3 flex flex-wrap items-center gap-x-4 gap-y-1 hover:bg-surface px-2 rounded-lg">
                <span className="font-semibold text-ink flex-1 min-w-[200px]">{p.name}{p.archived && <span className="ml-2 text-xs text-ink-muted">archived</span>}</span>
                <span className="text-sm tabular-nums">{money(p.unit_price_cents)} / {p.unit}</span>
                <span className="text-xs font-semibold text-ink-muted">{p.product_line}</span>
                <span className="text-xs text-ink-muted">{p.taxable ? 'Taxable' : 'Not taxable'}</span>
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function ProductForm({ seller, product, lines, onCancel, onSaved }) {
  const [form, setForm] = useState(product);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const fields = {
      seller_id: seller.id, name: form.name.trim(), description: form.description || null, unit_price_cents: toCents(form.price),
      unit: form.unit || 'each', taxable: !!form.taxable, product_line: form.product_line?.trim() || 'Other',
      tapzo_plan: form.tapzo_plan || null, archived: !!form.archived,
    };
    const { error: err } = form.id
      ? await supabase.from('invoice_products').update(fields).eq('id', form.id)
      : await supabase.from('invoice_products').insert(fields);
    setSaving(false);
    if (err) setError(err.message); else onSaved();
  };
  return (
    <form onSubmit={save}>
      <Card title={form.id ? `Edit ${product.name}` : 'New product or service'}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name"><input className={inputClass} value={form.name} onChange={set('name')} required /></Field>
          <Field label="Product line" hint="Groups revenue in reports.">
            <input list="product-lines" className={inputClass} value={form.product_line} onChange={set('product_line')} />
            <datalist id="product-lines">{lines.map((l) => <option key={l} value={l} />)}</datalist>
          </Field>
          <Field label="Description (shown on the invoice)" className="sm:col-span-2"><textarea rows={2} className={inputClass} value={form.description || ''} onChange={set('description')} /></Field>
          <Field label="Price (USD)"><input inputMode="decimal" className={inputClass} value={form.price} onChange={set('price')} placeholder="0.00" /></Field>
          <Field label="Per">
            <select className={inputClass} value={form.unit} onChange={set('unit')}>{UNITS.map((u) => <option key={u} value={u}>{u}</option>)}</select>
          </Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.taxable} onChange={set('taxable')} /> Taxable (sales tax applies)</label>
          <Field label="Carduzo plan (optional)" hint="Marks lines that are Carduzo subscriptions.">
            <select className={inputClass} value={form.tapzo_plan} onChange={set('tapzo_plan')}>
              <option value="">Not a Carduzo plan</option><option value="pro">Pro</option><option value="teams">Teams</option>
            </select>
          </Field>
          {form.id && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.archived} onChange={set('archived')} /> Archived</label>}
        </div>
        {error && <div className={errorBox}>{error}</div>}
        <div className="flex gap-2.5">
          <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
          <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
        </div>
      </Card>
    </form>
  );
}

/* ------------------------------------------------------------------ taxes */

export function Taxes({ seller, taxRates, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const fields = {
      seller_id: seller.id, name: editing.name.trim(), rate: Number(editing.rate), jurisdiction: editing.jurisdiction || null,
      is_default: !!editing.is_default, archived: !!editing.archived,
    };
    const { error: err } = editing.id
      ? await supabase.from('tax_rates').update(fields).eq('id', editing.id)
      : await supabase.from('tax_rates').insert(fields);
    setSaving(false);
    if (err) setError(err.message); else { setEditing(null); onChanged(); }
  };
  const set = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  return (
    <div className="space-y-5 max-w-3xl">
      <div className={noticeBox}>
        Invoices are sold by your business directly, so you collect and file their sales tax. Whether a product is taxable depends on the state:
        check with your accountant. Rates marked <strong>default</strong> are added to taxable lines on new invoices; tax-exempt clients pay none.
      </div>
      {editing && (
        <form onSubmit={save}>
          <Card title={editing.id ? `Edit ${editing.name}` : 'New tax rate'}>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Name"><input className={inputClass} value={editing.name} onChange={set('name')} required /></Field>
              <Field label="Rate (%)"><input inputMode="decimal" className={inputClass} value={editing.rate} onChange={set('rate')} required /></Field>
              <Field label="Jurisdiction"><input className={inputClass} value={editing.jurisdiction || ''} onChange={set('jurisdiction')} /></Field>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_default} onChange={set('is_default')} /> Default on new invoices</label>
              {editing.id && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.archived} onChange={set('archived')} /> Archived</label>}
            </div>
            {error && <div className={errorBox}>{error}</div>}
            <div className="flex gap-2.5">
              <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
              <button type="button" onClick={() => setEditing(null)} className={btnSecondary}>Cancel</button>
            </div>
          </Card>
        </form>
      )}
      <Card title="Sales tax rates" actions={<button type="button" className={btnPrimary} onClick={() => setEditing({ name: '', rate: '', jurisdiction: '', is_default: false })}>New rate</button>}>
        <div className="divide-y divide-line">
          {taxRates.map((t) => (
            <button key={t.id} type="button" onClick={() => setEditing({ ...t })} className="w-full text-left py-3 flex flex-wrap items-center gap-4 hover:bg-surface px-2 rounded-lg">
              <span className="font-semibold text-ink flex-1">{t.name}{t.archived && <span className="ml-2 text-xs text-ink-muted">archived</span>}</span>
              <span className="tabular-nums">{Number(t.rate)}%</span>
              <span className="text-sm text-ink-muted">{t.jurisdiction}</span>
              {t.is_default && <span className="text-xs font-semibold text-blue">Default</span>}
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
